import { EmptyState, LoadingState, Modal } from '@components/common';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchKotTrackingItems } from '../../../services/reportsService';
import type { KotTrackingRow, SalesItemView } from '../../../types/report';
import { apiError } from '../../../util/notify';
import {
  formatReportDate,
  formatReportDateTime,
  formatReportMode,
  formatReportMoney,
} from '../../../util/reporting';

interface KotTrackingDetailsModalProps {
  isOpen: boolean;
  row: KotTrackingRow | null;
  onClose: () => void;
}

const buildOrderLabel = (row: KotTrackingRow): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

export const KotTrackingDetailsModal: React.FC<
  KotTrackingDetailsModalProps
> = ({ isOpen, row, onClose }) => {
  const [items, setItems] = useState<SalesItemView[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchKOTItems = useCallback(
    async (cancelled: boolean) => {
      try {
        if (!isOpen || !row || cancelled) {
          setItems([]);
          return;
        }
        setIsLoading(true);
        const response = await fetchKotTrackingItems(row.order_id, row.kot_id);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load KOT items');
        }
        setItems(response.data);
      } catch (e) {
        setItems([]);
        apiError(
          e instanceof Error
            ? e.message
            : 'Sorry, Failed to fetch items. Please try again',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [isOpen, row],
  );

  useEffect(() => {
    if (!isOpen || !row) {
      setItems([]);
      return;
    }

    let cancelled = false;
    fetchKOTItems(cancelled);

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [fetchKOTItems, isOpen, row]);

  if (!isOpen || !row) return null;

  const totalAmount = items.reduce((sum, item) => sum + item.total_amount, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={buildOrderLabel(row)}>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Physical KOT #
            </p>
            <p className="mt-0.5 font-semibold text-gray-900">
              {row.physical_kot_number || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              System KOT ID
            </p>
            <p className="mt-0.5 font-semibold text-gray-900">#{row.kot_id}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Recorded by
            </p>
            <p className="mt-0.5 font-semibold text-gray-900">
              {row.recorded_by_name || 'Not recorded'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Date
            </p>
            <p className="mt-0.5 text-gray-900">
              {formatReportDate(row.order_date)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Confirmed at
            </p>
            <p className="mt-0.5 text-gray-900">
              {formatReportDateTime(row.confirmed_at)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Waiter
            </p>
            <p className="mt-0.5 text-gray-900">
              {row.waiter_name || 'Not assigned'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Table / Section
            </p>
            <p className="mt-0.5 text-gray-900">
              {row.table_name || formatReportMode(row.mode)}
              {row.section_name ? ` · ${row.section_name}` : ''}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Outlet
            </p>
            <p className="mt-0.5 text-gray-900">{row.outlet_name}</p>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            Items on this KOT
          </h3>
          {isLoading ? (
            <LoadingState size="sm" />
          ) : items.length === 0 ? (
            <EmptyState
              title="No items found"
              description="This KOT has no recorded items."
            />
          ) : (
            <div className="max-h-[24rem] overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit price</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-gray-900">
                        {item.item_name}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {item.quantity}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {formatReportMoney(item.unit_price)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">
                        {formatReportMoney(item.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td
                      colSpan={3}
                      className="px-3 py-2 text-right text-sm font-semibold text-gray-700"
                    >
                      Total
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900">
                      {formatReportMoney(totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
